import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {Col, Row} from "reactstrap";
import {deepCopy, formatMoney, stringComparator} from "../../../../../../../core/services/utils/utils";
import useIsMounted from "../../../../../../hooks/use-is-mounted";
import {Tab, Tabs} from "../../../../../../components/base/Tabs";
import IconButton from "../../../../../../components/base/button/icon_button";
import {ArrowBack, FileCopyOutlined, MailOutline} from "@mui/icons-material";
import {Box, CircularProgress, SvgIcon, Tooltip, Typography} from "@mui/material";
import {routeFunctions} from "../../../../../../../core/models/static/routes/app-routes";
import Api from "../../../../../../../core/services/api/api";
import ToastService from "../../../../../../../core/services/toast";
import {AccountingTransactionTypes, CustomerAddressTypes} from "../../../../../../../core/constants/enums";
import Button from "../../../../../../components/base/button/button";
import {ReactComponent as EmptyCart} from "../../../../../../../assets/images/art-like/empty_cart.svg";
import SendOrderEmailDialog from "../../../../../../components/dialogs/send-order-email";
import {ReactComponent as RemoveIcon} from "../../../../../../../assets/images/remove.svg";
import SummaryInfoCard, {summaryInfoCardBodyEntryTypes} from "../../../../../../components/app-specific/summary-cards/summary-info-card";
import QuoteInformationViewBillInfoSection from "./sections/bill-info";
import QuoteInformationViewProductsSection from "./sections/products";
import OrderInformationViewEditAddressesSection from "../../../../../../components/app-specific/order-inforamtion-shared-sections/edit-addresses";
import OrderInformationViewReadOnlyAddressesSection from "../../../../../../components/app-specific/order-inforamtion-shared-sections/read-only-addresses";
import OrderInformationViewTransactionsSection, {OrderInformationViewTransactionsTypes} from "../../../../../../components/app-specific/order-inforamtion-shared-sections/transactions";
import OrderInformationViewNotesSection, {OrderNoteAutoDefineTypes} from "../../../../../../components/app-specific/order-inforamtion-shared-sections/notes";
import {useConfigureNotebook} from "../../../../../../hooks/use-notebook-config";
import DateUtils from "../../../../../../../core/services/date-utils";
import useCachedPriceLevelsDictionaryController from "../../../../../../hooks/use-price-level-dictionary";
import useAccessLevels from "../../../../../../hooks/use-access-levels";
import {ReactComponent as Printer} from '../../../../../../../assets/images/printer.svg'
import {useAppSelector} from "../../../../../../redux";
import {Link, useHistory, useParams} from "react-router-dom";
import useQuery from "../../../../../../hooks/use-query";
import CreateMissingItemsInWarehousesDialog from "../../../../../../components/dialogs/create-missing-items-in-warehouses-dialog";
import OrderInformationViewShippingInfoCard from "../../../../../../components/app-specific/order-inforamtion-shared-sections/shipping-info-card/shipping-information";
import useBehavioralAnalyticsTool from "../../../../../../hooks/use-behavioural-analytics-tool";
import PrintOrderConfigurationsDialog from "../../../../../../components/dialogs/print-order-configurations";
import {Popover} from "../../../../../../components/app-specific/popover";
import {stringifyUrl} from "query-string";
import UpsertOrderInfoDialog from "../../../../../../components/dialogs/upsert-order-info";
import DuplicateOrderDialog, {DuplicateOrderVariants} from "../../../../../../components/dialogs/duplicate-order-dialog";
import EnvService from "../../../../../../../core/services/env";


const tabs = {
    products: 'products',
    shippingAddress: 'shippingAddress',
    billingAddress: 'billingAddress',
    addresses: 'addresses',
    transactions: 'transactions',
    notes: 'notes',
}

const orderInfoSummaryBodyIds = {
    termsCode: 'termsCode',
    requiredDate: 'requiredDate',
    salesPerson: 'salesPerson',
    territory: 'territory',
    accountManager: 'accountManager',
    orderDate: 'orderDate',
    type: 'type',
    status: 'status',
    poNo: 'poNo',
    fob: 'fob',
    referenceNo: 'referenceNo',
}

const userInfoSummaryBodyIds = {
    accountReceivable: 'accountReceivable',
    limitedCredit: 'limitedCredit',
    otherOrders: 'otherOrders',
    availableCredit: 'availableCredit',
    priceLevel: 'priceLevel',
    paymentTerms: 'paymentTerms',
}

const defaultTab = tabs.products

const initSendOrderEmailDialogState = {open: false};

const initCreateMissingItemsInWarehousesDialogState = {
    open: false, items: [], close: () => {
    }
};
const initPrintOrderConfigurationsDialogState = {open: false, printUrl: '', onClose: () => void 0};
const initUpsertOrderInfoDialogState = {open: false, entityToShow: ''};

const QuoteInformationView = () => {
    const params = useParams();
    const [query, setQuery] = useQuery();
    const history = useHistory();
    const [loading, setLoading] = useState(true);
    const [submittingDisabled, setSubmittingDisabled] = useState(false);
    const [order, _setOrder] = useState({});
    const [accountInfoLoading, setAccountInfoLoading] = useState(true);
    const [accountInfo, setAccountInfo] = useState({});
    const [removing, setRemoving] = useState(false);
    const [sendOrderEmailDialogState, setSendOrderEmailDialogState] = useState(initSendOrderEmailDialogState);
    const [CreateMissingItemsInWarehousesDialogState, setCreateMissingItemsInWarehousesDialogState] = useState(initCreateMissingItemsInWarehousesDialogState);
    const [warehouses, setWarehouses] = useState([])
    const [canEditUnitPrice, setCanEditUnitPrice] = useState(false)
    const [eligiblePriceChangePct, setEligiblePriceChangePct] = useState(null);
    const [generatingNextOrderEntity, setGeneratingNextOrderEntity] = useState(false);
    const [shippingInfo, setShippingInfo] = useState({});
    const [freeShippingEligibility, setFreeShippingEligibility] = useState({});
    const [freightEstimation, setFreightEstimation] = useState(null);
    const [loadingFreightEstimation, setLoadingFreightEstimation] = useState(false);
    const [loadingShippingInfo, setLoadingShippingInfo] = useState(true);
    const [printOrderConfigurationsDialog, setPrintOrderConfigurationsDialog] = useState(initPrintOrderConfigurationsDialogState);
    const [upsertOrderInfoDialog, setUpsertOrderInfoDialog] = useState(initUpsertOrderInfoDialogState);
    const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
    const isMounted = useIsMounted();
    const shippingInfoCompanyIdRef = React.useRef();

    const defaultCustomer = useAppSelector(state => state.customerInformation);
    const {configureNotebookTags} = useConfigureNotebook('invoice-information-view');
    const {configureBehavioralTool} = useBehavioralAnalyticsTool();
    const {getPriceLevelDictionary} = useCachedPriceLevelsDictionaryController()
    const operatorAccessLevels = useAccessLevels();

    const [productAvailabilities, setProductAvailabilities] = useState({});
    const productAvailabilitiesRef = useRef(productAvailabilities);
    productAvailabilitiesRef.current = productAvailabilities;


    const tabsList = useMemo(() => {
        const res = [
            {label: "Products", value: tabs.products},
            {label: "Addresses", value: tabs.addresses},
            {label: "Shipping Addresses", value: tabs.shippingAddress},
            {label: "Billing Addresses", value: tabs.billingAddress},
            {label: "Transactions", value: tabs.transactions},
            {label: "Notes", value: tabs.notes},
        ];
        if (order?.isClosed || !operatorAccessLevels.packages.sales.manageQuotes) {
            res.splice(res.indexOf(res.find(e => e.value === tabs.shippingAddress)), 1);
            res.splice(res.indexOf(res.find(e => e.value === tabs.billingAddress)), 1);
        } else {
            res.splice(res.indexOf(res.find(e => e.value === tabs.addresses)), 1);
        }
        return res;
    }, [operatorAccessLevels.packages.sales.manageQuotes, order?.isClosed])

    const orderId = useMemo(() => Number(params.orderId), [params.orderId])
    const customerId = useMemo(() => Number(params.customerId), [params.customerId])

    const tab = useMemo(() => {
        let selected = query?.tab;
        if (!selected || !tabsList?.map(e => e.value).includes(selected)) {
            selected = defaultTab;
        }
        return selected;
    }, [query?.tab, tabsList])

    /**
     * As soon as the component is mounted:
     * - Configures the notebook tags for the view
     * - Configures the behavioral analytics tool
     */
    useEffect(() => {
        if (!defaultCustomer?.code)
            return;
        configureNotebookTags((noteTags) => noteTags.customerSalesQuoteInformation(defaultCustomer?.code, orderId));
        configureBehavioralTool((tags) => tags.customerSalesQuoteInformation(defaultCustomer?.code, orderId));
    }, [orderId, defaultCustomer?.code, configureNotebookTags, configureBehavioralTool]);

    /**
     * Fetches the free shipping eligibility of the order from the server.
     * @type {function(*=): Promise<void>}
     */
    const getFreeShippingEligibility = useCallback(async (freightCompanyId = undefined) => {
        if (!(operatorAccessLevels.packages.sales.quoteFreightInfo)) {
            return;
        }
        const response = await Api.calculateFreeFreightEligibility({
            orderId: orderId,
            freightCompanyId: freightCompanyId ?? shippingInfoCompanyIdRef.current,
        })
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setFreeShippingEligibility(response.data);
            setShippingInfo(prevState => ({...prevState, freeFreightId: response.data.id}));
        } else {
            setFreeShippingEligibility({});
        }
    }, [isMounted, operatorAccessLevels.packages.sales.quoteFreightInfo, orderId])

    /**
     * Fetches the estimated freight fee for the quote based on freight company and shipping address.
     * @type {function(number=): Promise<void>}
     */
    const getFreightEstimation = useCallback(async (freightCompanyId = undefined) => {
        if (!operatorAccessLevels.packages.sales.quoteFreightInfo)
            return;
        const resolvedFreightCompanyId = freightCompanyId ?? shippingInfoCompanyIdRef.current;
        if (!resolvedFreightCompanyId) {
            setFreightEstimation(null);
            return;
        }
        setLoadingFreightEstimation(true);
        const response = await Api.estimateShippingFee({
            orderId: orderId,
            freightCompanyId: resolvedFreightCompanyId,
        });
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setFreightEstimation(response.data);
        } else {
            setFreightEstimation(null);
        }
        setLoadingFreightEstimation(false);
    }, [isMounted, operatorAccessLevels.packages.sales.quoteFreightInfo, orderId])

    /**
     * Sets the order state with its transactions sorted.
     * @type {function(*=): void}
     */
    const setOrder = useCallback((stateOrFunctionWithPrevState) => {
        _setOrder(prevState => {
            const newState = typeof stateOrFunctionWithPrevState === "function" ? stateOrFunctionWithPrevState(prevState) : stateOrFunctionWithPrevState;
            // sort the appliedTransactions by their type of the id so that the gift card can be at the beginning
            // or at the end.
            newState.appliedTransactions?.sort((a, b) => stringComparator(a.transactionTypeName, b.transactionTypeName));
            if (shippingInfoCompanyIdRef.current) {
                void getFreeShippingEligibility(shippingInfoCompanyIdRef.current);
                void getFreightEstimation(shippingInfoCompanyIdRef.current);
            }
            return {...newState}
        });
    }, [getFreeShippingEligibility, getFreightEstimation])

    /**
     * Fetches the order's shipping information from the server.
     * @type {function(*=): Promise<void>}
     */
    const getOrderShippingInformation = useCallback(async (orderInfo) => {
        if (!(operatorAccessLevels.packages.sales.quoteFreightInfo)) {
            setLoadingShippingInfo(false);
            return;
        }
        setLoadingShippingInfo(true);
        const response = await Api.getFreightOrderInformation({orderId: orderId});
        if (!isMounted())
            return;
        setShippingInfo({
            id: response?.data?.id,
            freeFreightId: response?.data?.freeFreightRequestId,
            freightCompany: {
                id: response?.data?.freightCompanyId,
                title: response?.data?.freightCompanyTitle,
                code: response?.data?.freightCompanyCode ?? orderInfo?.freightCompanyCode
            },
            estimatedDate: response?.data?.estimatedDeliveryDateTime ?? orderInfo?.shipDate,
            estimatedFee: response?.data?.estimatedFee,
            customerFee: response?.data?.customerFee ?? orderInfo?.freightFee,
            trackingNo: response?.data?.trackingNumber ?? orderInfo?.trackingNo,
            shippingBillTo: response?.data?.shippingBillTo ?? null,
            metaData: response?.data?.metaData ?? null,
        });
        shippingInfoCompanyIdRef.current = response?.data?.freightCompanyId;
        if (response?.resultFlag && response.data?.freightCompanyId) {
            await Promise.all([
                getFreeShippingEligibility(response.data.freightCompanyId),
                getFreightEstimation(response.data.freightCompanyId),
            ]);
        } else {
            setFreightEstimation(null);
        }
        setLoadingShippingInfo(false);
    }, [isMounted, orderId, operatorAccessLevels.packages.sales.quoteFreightInfo, getFreeShippingEligibility, getFreightEstimation])

    /**
     * Fetches the available quantities of order products from the api.
     * - sets the items' availability in the state.
     * @type {(partNos: any) => Promise<void>}
     */
    const getProductsAvailabilities = useCallback(async (partNos) => {
        if (!partNos || !partNos.length)
            return;

        setProductAvailabilities(prevState => {
            for (const partNo of partNos) {
                prevState[partNo] = {
                    ...(prevState[partNo] ?? {}),
                    loading: true,
                }
            }
            return {...prevState};
        })
        const response = await Api.getProductAvailabilitiesV2({
            partNumbers: partNos,
        });
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setProductAvailabilities(prevState => {
                for (const partNo of partNos) {
                    const data = response.data?.find(e => e.partNumber === partNo);
                    prevState[partNo] = {
                        ...(prevState[partNo] ?? {}),
                        availability: data?.localWarehouses ?? [],
                    }
                }
                return {...prevState};
            })
        }
        setProductAvailabilities(prevState => {
            for (const partNo of partNos) {
                prevState[partNo] = {
                    ...(prevState[partNo] ?? {}),
                    loading: false,
                }
            }
            return {...prevState};
        })
    }, [isMounted])

    /**
     * With each change in the [items] list of the [order] prop value:
     * - determines which one of the availabilities must be removed and which ones must be added.
     */
    useEffect(() => {
        const partNos = Array.from(new Set(order?.items?.map(e => e.partNo) ?? []));
        const _productAvailabilities = deepCopy(productAvailabilitiesRef.current);

        // remove the availabilities of removed products
        for (const partNo of Object.keys(_productAvailabilities ?? {})) {
            if (!partNos.includes(partNo)) {
                // must be removed
                delete _productAvailabilities[partNo];
            }
        }

        const newPartNos = partNos.filter(partNo => _productAvailabilities[partNo] === undefined);
        if (newPartNos.length)
            getProductsAvailabilities(newPartNos).then();
    }, [getProductsAvailabilities, order?.items])

    /**
     * Gets the sales person's warehouses and access levels.
     * @type {(function(): Promise<void>)}
     */
    const getSalesPersonWarehousesAndAccessLevels = useCallback(async () => {
        const response = await Api.getSalesPersonProfile()
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setWarehouses(response.data?.warehouseAccess ?? []);
            setCanEditUnitPrice(response.data?.accessToPriceChange ?? operatorAccessLevels.packages.sales.manageUnitPriceInQuote);
            setEligiblePriceChangePct(response.data?.eligiblePriceChangePct ?? null);
        }
    }, [isMounted, operatorAccessLevels.packages.sales.manageUnitPriceInQuote])

    /**
     * Fetches the sales person's warehouses and access levels with the first render.
     */
    useEffect(() => {
        void getSalesPersonWarehousesAndAccessLevels();
    }, [getSalesPersonWarehousesAndAccessLevels])

    /**
     * Fetches the customer account information from the server.
     * @type {function(): Promise<void>}
     */
    const getCustomerAccountInformation = useCallback(async () => {
        setAccountInfoLoading(true);
        const [response, priceLevelDict] = await Promise.all([
            Api.getCustomerAccountInformation(customerId),
            getPriceLevelDictionary(),
        ]);
        if (!isMounted()) return;
        if (response?.resultFlag) {
            setAccountInfo({
                ...response.data,
                priceLevel: {
                    ...response.data.priceLevel,
                    title: priceLevelDict?.find(e => e.priceLevelId === response.data.priceLevel.id)?.title ?? response.data.priceLevel.title,
                },
            })
        }
        setAccountInfoLoading(false);
    }, [getPriceLevelDictionary, isMounted, customerId])

    /**
     * As soon as the component is mounted:
     * - fetches customer account information from the server.
     */
    useEffect(() => {
        void getCustomerAccountInformation();
    }, [getCustomerAccountInformation])

    /**
     * Fetches the full information of the current order from the server.
     * - if the response is successful, sets the order in the state
     * @type {function(): Promise<void>}
     */
    const getOrderInformation = useCallback(async (setLoadingState = true) => {
        if (setLoadingState)
            setLoading(true);
        const response = await Api.getQuoteInformationForCustomer(customerId, orderId);
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setOrder(response.data);
            void getOrderShippingInformation(response?.data);
        }
        if (setLoadingState)
            setLoading(false);
    }, [customerId, getOrderShippingInformation, isMounted, orderId, setOrder])

    /**
     * With each change in the [customerId] or the [orderId] of the url params:
     * - fetches customer account information and order information from the server.
     */
    useEffect(() => {
        void getOrderInformation();
    }, [getOrderInformation])

    /**
     * Updates the order's information.
     *
     * - if the result of the api is successful, sets the new information.
     * @param {any} entry
     * @param {string} newValue
     * @return {boolean}
     */
    const updateOrderInfo = useCallback(async (entry, newValue) => {
        let name;
        switch (entry.id) {
            case orderInfoSummaryBodyIds.poNo:
                name = 'customerPO';
                break;
            case orderInfoSummaryBodyIds.fob:
                name = 'fob';
                break;
            case orderInfoSummaryBodyIds.requiredDate:
                name = 'requiredDate';
                break;
            case orderInfoSummaryBodyIds.referenceNo:
                name = 'referenceNo';
                break;
            case orderInfoSummaryBodyIds.territory: {
                name = 'territory';
                let abort = false;
                [newValue, abort] = await new Promise(resolve => {
                    setUpsertOrderInfoDialog({
                        open: true,
                        entityToShow: 'territory',
                        territory: order.territory ? {code: order.territory} : null,
                        close: () => {
                            resolve([undefined, true])
                            setUpsertOrderInfoDialog(initUpsertOrderInfoDialogState)
                        },
                        update: (values) => {
                            resolve([values.territory?.code, false])
                        },
                    })
                })
                if (abort)
                    return true;
                break;
            }
            case orderInfoSummaryBodyIds.accountManager: {
                name = 'accountManager';
                let abort = false;
                [newValue, abort] = await new Promise(resolve => {
                    setUpsertOrderInfoDialog({
                        open: true,
                        entityToShow: 'accountManager',
                        accountManager: order.accountManager,
                        close: () => {
                            resolve([undefined, true])
                            setUpsertOrderInfoDialog(initUpsertOrderInfoDialogState)
                        },
                        update: (values) => resolve([values.accountManager, false]),
                    })
                })
                if (abort)
                    return true;
                break;
            }
            case orderInfoSummaryBodyIds.termsCode: {
                name = 'termsCode';
                let abort = false;
                [newValue, abort] = await new Promise(resolve => {
                    setUpsertOrderInfoDialog({
                        open: true,
                        entityToShow: 'termsCode',
                        termsCode: order.termsCode,
                        close: () => {
                            resolve([undefined, true])
                            setUpsertOrderInfoDialog(initUpsertOrderInfoDialogState)
                        },
                        update: (values) => resolve([values.termsCode, false]),
                    })
                })
                if (abort)
                    return true;
                break;
            }
            default:
                return;
        }
        const forApi = {
            customerId: customerId,
            orderId: orderId,
            customerPo: entry.id === orderInfoSummaryBodyIds.poNo ? newValue ?? '' : order.customerPO,
            fob: entry.id === orderInfoSummaryBodyIds.fob ? newValue ?? '' : order.fob,
            requiredDate: entry.id === orderInfoSummaryBodyIds.requiredDate ? newValue ?? null : order.requiredDate,
            referenceNo: entry.id === orderInfoSummaryBodyIds.referenceNo ? newValue ?? '' : order.referenceNo,
            territoryCode: entry.id === orderInfoSummaryBodyIds.territory ? newValue ?? '' : order.territory,
            accountManagerCode: entry.id === orderInfoSummaryBodyIds.accountManager ? newValue?.code ?? null : order.accountManager?.code,
            paymentTerms: entry.id === orderInfoSummaryBodyIds.termsCode ? newValue ?? '' : order.termsCode,
        };
        if (entry.id === orderInfoSummaryBodyIds.requiredDate && !newValue) {
            newValue = order.orderDate;
        }
        setSubmittingDisabled(true);
        const response = await Api.updateQuotesPOAndFob(forApi);
        if (!isMounted()) return;
        setSubmittingDisabled(false);
        if (response?.resultFlag) {
            setOrder(prevState => ({...prevState, [name]: newValue}));
        }
        if (entry.id === orderInfoSummaryBodyIds.termsCode) {
            // fetch the order silently to update the general information.
            void getOrderInformation(false);
        }
        return response?.resultFlag;
    }, [customerId, orderId, order.customerPO, order.fob, order.requiredDate, order.referenceNo, order.territory, order.accountManager, order.termsCode, order.orderDate, isMounted, setOrder, getOrderInformation])

    /**
     * Update the shipping information of the current order.
     * @type {function(*=): Promise<void>}
     */
    const updateOrdersShippingInformation = useCallback(async (values) => {
        if (!operatorAccessLevels.packages.sales.manageQuotes || !operatorAccessLevels.packages.sales.quoteFreightInfo) {
            return;
        }
        const forApi = {
            orderId: order.id,
            freightCompanyInfo: values?.freightCompany,
            freightFee: Number(values?.customerFee),
            trackingNo: values?.trackingNo,
            estimatedDeliveryDateTime: values?.estimatedDate,
            estimatedFee: Number(values?.estimatedFee),
            freightInfoId: shippingInfo.id,
            freeFreightId: shippingInfo.freeFreightId,
            shippingBillTo: values?.shippingBillTo ?? null,
            metaData: values?.metaData ?? null,
        }
        setSubmittingDisabled(true);
        const response = await Api.saveOrderFreightInfo(forApi);
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            if (forApi.freightFee !== Number(shippingInfo.customerFee)) {
                await getOrderInformation();
            } else {
                setShippingInfo(prevState => ({
                    ...prevState,
                    id: prevState.id ?? response.data?.id,
                    freightCompany: values?.freightCompany,
                    estimatedDate: values?.estimatedDate,
                    estimatedFee: Number(values?.estimatedFee),
                    customerFee: Number(values?.customerFee),
                    trackingNo: values?.trackingNo,
                    shippingBillTo: values?.shippingBillTo ?? null,
                    metaData: values?.metaData ?? null,
                }))
                shippingInfoCompanyIdRef.current = values?.freightCompany?.id;
            }
        }
        setSubmittingDisabled(false);
    }, [order.id, shippingInfo.id, shippingInfo.freeFreightId, shippingInfo.customerFee, isMounted, getOrderInformation, operatorAccessLevels.packages.sales.manageQuotes, operatorAccessLevels.packages.sales.quoteFreightInfo])

    /**
     * Updates the freight company of the order and refetches the free shipping eligibility and freight estimation based on the new freight company.
     * @type {(function(*): void)|*}
     */
    const onFreightCompanyChanged = useCallback((freightCompany) => {
        shippingInfoCompanyIdRef.current = freightCompany?.id;
    }, [])


    /**
     * Removes the selected transaction from the order
     *
     * - if the result of the api is successful, removes the transaction from transactions list of the order.
     * @type {function(*=): Promise<void>}
     */
    const removeTransactionFromOrder = useCallback(async (transactionId) => {
        const numericTransactionId = Number(transactionId);
        if (!Number.isFinite(numericTransactionId)) {
            ToastService.error({
                title: 'Refund failed',
                message: 'The transaction could not be refunded because its identifier is invalid.',
            });
            return;
        }
        const forApi = {
            transactionId: numericTransactionId,
            orderId: orderId,
            customerId: customerId,
        }
        setSubmittingDisabled(true);
        const response = await Api.removeQuoteTransactionItem(forApi);
        if (!isMounted())
            return;
        setSubmittingDisabled(false);
        if (response?.resultFlag) {
            setOrder(response.data);
        }
    }, [orderId, customerId, isMounted, setOrder])

    /**
     * Sends an email of this order's information to the provided email address of the user by calling its appropriate
     * api.
     * @type {function(*=, *=): Promise<boolean>}
     */
    const sendEmail = useCallback(async (emailAddress, includeBCC) => {
        const forApi = {
            customerId: customerId,
            orderId: orderId,
            sendBccToOperator: includeBCC,
            email: emailAddress,
        };
        const response = await Api.sendQuoteInformationToUserEmail(forApi);
        return response?.resultFlag;
    }, [customerId, orderId])

    /**
     * Generates the next order entity from this entity in the server.
     *
     * - if the result of the api is successful navigates the user to the next order entity's information view.
     * @type {function(*=): Promise<void>}
     */
    const generateNextOrderEntity = useCallback(async (showErrorToast = true) => {
        const itemsWithComingSoonWarehouses = order?.items?.filter(e => productAvailabilities[e.partNo]?.availability?.find(avail => avail.warehouseCode === e.whse)?.isComingSoon) ?? [];
        if (itemsWithComingSoonWarehouses.length) {
            // open the dialog and on close, try to generate again.
            return new Promise(r => {
                setCreateMissingItemsInWarehousesDialogState({
                    open: true,
                    items: Array.from(itemsWithComingSoonWarehouses),
                    close: async () => {
                        r();
                        setCreateMissingItemsInWarehousesDialogState(initCreateMissingItemsInWarehousesDialogState);
                        await getProductsAvailabilities(itemsWithComingSoonWarehouses.map(e => e.partNo));
                    }
                });
            })
        }
        const forApi = {
            customerId: customerId,
            orderId: orderId,
            userToBackOrderPermission: true,
            note: "",
        }
        setGeneratingNextOrderEntity(true);
        setSubmittingDisabled(true);
        const response = await Api.generateSalesOrder(forApi, showErrorToast);
        if (!isMounted())
            return;
        setGeneratingNextOrderEntity(false);
        setSubmittingDisabled(false);
        if (response?.resultFlag) {
            history.replace(routeFunctions.customer.sales.salesOrder.single(customerId, orderId))
        }
    }, [order?.items, customerId, orderId, isMounted, productAvailabilities, getProductsAvailabilities, history])

    /**
     * Pays for a current order with the provided values.
     *
     * - If the result of the api is successful, calls the [generateNextOrderEntity] callback to proceed to the next
     * step of this order if applicable.
     * @type {function(*=): Promise<boolean>}
     */
    const payOrder = useCallback(async (values) => {
        let forApi = {
            ...values,
            customerId: customerId,
            orderId: orderId
        };
        setSubmittingDisabled(true);
        const response = await Api.payQuote(forApi);
        if (!isMounted())
            return [];
        setSubmittingDisabled(false);
        const sendEmailConfirmation = {
            send: sendEmail,
        };
        if (response?.resultFlag) {
            setOrder(response.data);
        }
        return [
            response?.resultFlag ?? false,
            sendEmailConfirmation,
            () => generateNextOrderEntity(false)
        ]
    }, [customerId, orderId, isMounted, sendEmail, setOrder, generateNextOrderEntity])

    /**
     * Removes the order from the server.
     * - if the result of the api is successful, navigates the user to the list view.
     * @type {function(): Promise<void>}
     */
    const removeOrder = useCallback(async () => {
        setRemoving(true);
        setSubmittingDisabled(true);
        const response = await Api.removeQuoteByCustomer(customerId, orderId);
        if (!isMounted())
            return;
        setRemoving(false);
        setSubmittingDisabled(false);
        if (response?.resultFlag) {
            return history.replace(routeFunctions.customer.sales.quotes.base(customerId));
        }
    }, [customerId, orderId, isMounted, history])

    /**
     * Removes the applied gift card from the order.
     *
     * - if the gift card is applied after tax, then it must be removed like a transaction, otherwise it must be
     * removed as a product.
     * @param {any} giftCard
     */
    const removeGiftCard = useCallback(async (giftCard) => {
        if (giftCard.isAplliedAfterTax) {
            // from transactions (refillable gift-card)
            const transactionId = order?.appliedTransactions?.find(e => e.transactionTypeName === AccountingTransactionTypes.giftCard).id;
            return await removeTransactionFromOrder(transactionId);
        }
        const forApi = {
            customerId: customerId,
            orderId: orderId,
            partNumbersRemoveList: [giftCard.equalPartNo]
        };
        setSubmittingDisabled(true);
        const response = await Api.updateQuoteItems(forApi);
        if (!isMounted())
            return;
        setSubmittingDisabled(false);
        if (response?.resultFlag) {
            setOrder(response.data);
            if (response.data.isDeleted) {
                return history.replace(routeFunctions.customer.sales.quotes.base(customerId));
            }
        }
    }, [customerId, history, isMounted, order?.appliedTransactions, orderId, removeTransactionFromOrder, setOrder])

    /**
     * Updates the address of this order in the server.
     *
     * - If the result of the api is successful, sets the new address information in the order state.
     * @param address
     */
    const updateAddress = useCallback(async (address) => {
        const forApi = {
            customerId: customerId,
            orderId: orderId,
            address: address,
        }
        setSubmittingDisabled(true);
        const response = await Api.updateQuoteAddress(forApi);
        if (!isMounted())
            return;
        setSubmittingDisabled(false);
        if (response?.resultFlag) {
            setOrder(response.data);
        }
        return response?.resultFlag;
    }, [customerId, orderId, isMounted, setOrder])

    /**
     * Changes the tab of the view.
     * @type {function(*=, *=): void}
     */
    const onTabChange = useCallback((_, newValue) => {
        setQuery(prevState => ({...prevState, tab: newValue}));
    }, [setQuery]);

    /**
     * Opens the send-email-dialog.
     */
    const openEmailConfirmation = useCallback(() => {
        setSendOrderEmailDialogState({
            open: true,
        });
    }, [])

    /**
     * Gets users confirmation before removing the current order entirely.
     */
    const getRemoveConfirmation = useCallback(() => {
        window.partner?.openConfirmationDialog(
            <div className={'p-3'}>
                <p className={'text-sm text-color-2'}>
                    Are you sure that you want to remove this quote?
                </p>
            </div>,
            {
                maxWidth: 'sm'
            },
            (v) => v && removeOrder(),
        );
    }, [removeOrder])

    const orderInfoSummary = useMemo(() => ({
        header: {
            title: 'Order No',
            value: order?.orderNo,
            loadingValue: "Loading...",
            copyable: true,
        },
        body: [
            {
                id: orderInfoSummaryBodyIds.termsCode,
                title: 'Payment Terms',
                value: order?.termsCode,
                editable: EnvService.DEV_editOrderTermsCode && !!order && !order.isClosed && operatorAccessLevels.packages.sales.manageQuotes,
                edit: updateOrderInfo,
                openDialog: true,
            },
            {
                id: orderInfoSummaryBodyIds.territory,
                title: 'Territory',
                value: order?.territory,
                editable: !!order && operatorAccessLevels.packages.base.manageTerritories,
                clearable: !!order,
                edit: updateOrderInfo,
                openDialog: true,
            },
            {
                id: orderInfoSummaryBodyIds.salesPerson,
                title: 'Sales person',
                value: order?.salesPersonCode,
            },
            {
                id: orderInfoSummaryBodyIds.accountManager,
                title: 'Account manager',
                value: order?.accountManager?.code,
                editable: !!order && operatorAccessLevels.packages.base.manageTerritories,
                clearable: !!order,
                edit: updateOrderInfo,
                openDialog: true,
            },
            {
                id: orderInfoSummaryBodyIds.orderDate,
                title: 'Order date',
                value: order?.orderDate,
                format: (val) => val ? DateUtils.serverToLocal(val).format(DateUtils.dateFormat) : '--',
            },
            {
                id: orderInfoSummaryBodyIds.requiredDate,
                title: 'Required date',
                value: order?.requiredDate,
                format: (val) => val ? DateUtils.serverToLocal(val).format(DateUtils.dateFormat) : '--',
                type: summaryInfoCardBodyEntryTypes.date,
                editable: !!order && !order.isClosed && operatorAccessLevels.packages.sales.manageQuotes,
                clearable: !!order && !order.isClosed,
                edit: updateOrderInfo,
            },
            {
                id: orderInfoSummaryBodyIds.type,
                title: 'Type',
                value: order?.type,
            },
            {
                id: orderInfoSummaryBodyIds.status,
                title: 'Status',
                value: order?.status,
            },
            {
                id: orderInfoSummaryBodyIds.poNo,
                title: 'Po number',
                value: order?.customerPO,
                editable: order && !order.isClosed && operatorAccessLevels.packages.sales.manageQuotes,
                clearable: order && !order.isClosed,
                edit: updateOrderInfo,
            },
            {
                id: orderInfoSummaryBodyIds.fob,
                title: 'Fob',
                value: order?.fob,
                editable: order && !order.isClosed && operatorAccessLevels.packages.sales.manageQuotes,
                clearable: order && !order.isClosed,
                edit: updateOrderInfo,
            },
            {
                id: orderInfoSummaryBodyIds.referenceNo,
                title: 'Reference No',
                value: order?.referenceNo,
                editable: order && !order.isClosed && operatorAccessLevels.packages.sales.manageQuotes,
                clearable: order && !order.isClosed,
                edit: updateOrderInfo,
            },
        ],
    }), [order, operatorAccessLevels.packages.base.manageTerritories, operatorAccessLevels.packages.sales.manageQuotes, updateOrderInfo])

    const userInfoSummary = useMemo(() => ({
        header: {
            title: 'Customer',
            value: accountInfo?.customer?.name,
            loadingValue: "Loading...",
        },
        body: [
            {
                id: userInfoSummaryBodyIds.accountReceivable,
                title: 'Account receivable',
                value: accountInfo?.accountReceivable,
                format: formatMoney,
            },
            {
                id: userInfoSummaryBodyIds.limitedCredit,
                title: 'Credit limit',
                value: accountInfo?.creditLimit,
                format: formatMoney,
            },
            {
                id: userInfoSummaryBodyIds.otherOrders,
                title: 'Other orders',
                value: accountInfo?.otherOrders,
                format: formatMoney,
            },
            {
                id: userInfoSummaryBodyIds.availableCredit,
                title: 'Available credit',
                value: accountInfo?.availableCredit,
                format: formatMoney,
            },
            {
                id: userInfoSummaryBodyIds.priceLevel,
                title: 'Price level',
                value: accountInfo?.priceLevel?.title,
            },
            {
                id: userInfoSummaryBodyIds.paymentTerms,
                title: 'Payment terms',
                value: accountInfo?.paymentTermsCode,
            },
        ],
    }), [accountInfo])

    /**
     * Opens the print configuration dialog.
     * @type {(function(): void)}
     */
    const openPrintConfigDialog = useCallback(() => {
        setPrintOrderConfigurationsDialog({
            open: true,
            printUrl: routeFunctions.print.quote(customerId, orderId),
            onClose: () => setPrintOrderConfigurationsDialog(initPrintOrderConfigurationsDialogState)
        })
    }, [customerId, orderId])


    return (
        <>
            <div className='order-information-view sales-views-top-progress-indicator-offset'>
                {
                    !loading && (order && order.isDeleted) &&
                    <>
                        <Col className={'h-100 w-100 align-items-center justify-content-center d-flex flex-column'}>
                            <EmptyCart className={'svg'} style={{width: 'min(500px, 90vw)'}}/>
                            <p className='text-sm text-color-1 text-center'>
                                The selected Order has been removed. In case of any problems, please contact our technical support.
                            </p>
                            <div className='w-100 my-3'/>
                            {
                                operatorAccessLevels.packages.sales.quotesList &&
                                <Button
                                    variant={'outlined'}
                                    color={'primary'}
                                    component={Link}
                                    to={routeFunctions.customer.sales.quotes.base(customerId)}
                                >
                                    See {defaultCustomer?.name}'s Orders
                                </Button>
                            }
                        </Col>
                    </>
                }
                {
                    !(!loading && !!order?.isDeleted) &&
                    <>
                        <Row className='no-gutters w-100 align-items-center justify-content-between'>
                            <Row className='justify-content-start align-items-center no-gutters'>
                                <Tooltip title={`Back to ${defaultCustomer?.name}'s quotes`}>
                                    <Box component={'span'}>
                                        <IconButton
                                            color={'paper.secondary'}
                                            component={Link}
                                            to={routeFunctions.customer.sales.quotes.base(customerId)}
                                            replace
                                        >
                                            <ArrowBack className='icon normal'/>
                                        </IconButton>
                                    </Box>
                                </Tooltip>
                                <Typography variant={'h6'} fontWeight={600} marginInlineStart={1}>
                                    Quote Information
                                </Typography>
                            </Row>
                            <Row className='justify-content-end align-items-center no-gutters' style={{gap: 8}}>
                                {
                                    operatorAccessLevels.packages.sales.createOrderFromAnother &&
                                    <Tooltip arrow title={'Duplicate this Quote'}>
                                        <IconButton
                                            onPressed={() => setDuplicateDialogOpen(true)}
                                            color={'paper.secondary'}
                                        >
                                            <FileCopyOutlined className={'icon large'}/>
                                        </IconButton>
                                    </Tooltip>
                                }
                                <Tooltip arrow title={'Send order information in an email'}>
                                    <IconButton
                                        onPressed={openEmailConfirmation}
                                        color={'paper.secondary'}
                                    >
                                        <MailOutline className='icon large'/>
                                    </IconButton>
                                </Tooltip>
                                {
                                    operatorAccessLevels.packages.bizPrint.printQuote &&
                                    <Popover
                                        child={(onEnter) => (
                                            <Tooltip arrow title={'Print Quote'}>
                                                <IconButton
                                                    onPressed={onEnter}
                                                    color={'paper.secondary'}
                                                >
                                                    <SvgIcon className='icon large' component={Printer}/>
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                        popoverChild={() => (
                                            <Row
                                                className='no-gutters align-items-center justify-content-center px-2'
                                                style={{maxWidth: '13rem'}}>
                                                <Col xs={12} className={'mx-2 my-2'}>
                                                    <Button
                                                        color={'secondary'}
                                                        variant={'text'}
                                                        className={'w-100'}
                                                        component={Link}
                                                        target={'_blank'}
                                                        to={stringifyUrl({
                                                            url: routeFunctions.print.quote(params.customerId, params.orderId),
                                                            query: {showAddresses: true, showPaymentTerms: true, showPrices: true, showTransactions: false}
                                                        })}
                                                    >
                                                        Without transactions
                                                    </Button>
                                                </Col>
                                                <Col xs={12} className={'mx-2 mb-2'}>
                                                    <Button
                                                        color={'secondary'}
                                                        variant={'text'}
                                                        className={'w-100'}
                                                        component={Link}
                                                        target={'_blank'}
                                                        to={stringifyUrl({
                                                            url: routeFunctions.print.quote(params.customerId, params.orderId),
                                                            query: {showAddresses: true, showPaymentTerms: true, showPrices: true, showTransactions: true}
                                                        })}
                                                    >
                                                        With transactions
                                                    </Button>
                                                </Col>
                                                <Col xs={12} className={'mx-2 mb-2'}>
                                                    <Button
                                                        color={'secondary'}
                                                        variant={'text'}
                                                        className={'w-100'}
                                                        onClick={openPrintConfigDialog}
                                                    >
                                                        Custom
                                                    </Button>
                                                </Col>
                                            </Row>
                                        )}
                                        anchorOrigin={{
                                            vertical: 'bottom',
                                            horizontal: 'right',
                                        }}
                                        transformOrigin={{
                                            vertical: 'top',
                                            horizontal: 'right',
                                        }}
                                    />
                                }
                                {
                                    !order?.isClosed && operatorAccessLevels.packages.sales.manageQuotes &&
                                    <Tooltip arrow title={'Remove order'}>
                                        <IconButton
                                            onPressed={getRemoveConfirmation}
                                            color={'paper.error'}
                                        >
                                            {
                                                removing
                                                    ? <CircularProgress size={20} color={'secondary'}/>
                                                    : <SvgIcon component={RemoveIcon} className='icon large'/>
                                            }
                                        </IconButton>
                                    </Tooltip>
                                }
                            </Row>
                        </Row>
                        <div className='w-100 my-3'/>
                        <div className='d-flex flex-wrap w-100 justify-content-start'>
                            <Col xs={12} md={6} xl={4} className={'mb-3'}>
                                <SummaryInfoCard
                                    header={orderInfoSummary.header}
                                    body={orderInfoSummary.body}
                                    loading={loading}
                                    loadingCount={orderInfoSummary.body.length}
                                />
                            </Col>
                            <Col xs={12} md={6} xl={4} className={'mb-3'}>
                                <SummaryInfoCard
                                    header={userInfoSummary.header}
                                    body={userInfoSummary.body}
                                    loading={accountInfoLoading}
                                    loadingCount={userInfoSummary.body.length}
                                />
                            </Col>
                            <Col xs={12} md={6} xl={4} className={'mb-3'} style={{overflowY: 'hidden'}}>
                                <QuoteInformationViewBillInfoSection
                                    order={order}
                                    setOrder={setOrder}
                                    loading={loading}
                                    generateNextOrderEntity={generateNextOrderEntity}
                                    generatingNextOrderEntity={generatingNextOrderEntity}
                                    removeGiftCard={removeGiftCard}
                                    removeTransaction={removeTransactionFromOrder}
                                    getOrderInformation={getOrderInformation}
                                    payOrder={payOrder}
                                    customerId={customerId}
                                    setSubmittingDisabled={setSubmittingDisabled}
                                    submittingDisabled={submittingDisabled}
                                />
                            </Col>
                            {
                                operatorAccessLevels.packages.sales.quoteFreightInfo &&
                                <Col xs={12} md={6} xl={8} className={'mb-3'}>
                                    <OrderInformationViewShippingInfoCard
                                        loading={loading || loadingShippingInfo}
                                        shippingInfo={shippingInfo}
                                        freeShippingEligibility={freeShippingEligibility}
                                        getFreeShippingEligibility={getFreeShippingEligibility}
                                        getFreightEstimation={getFreightEstimation}
                                        freightEstimation={freightEstimation}
                                        loadingFreightEstimation={loadingFreightEstimation}
                                        viewOnly={!operatorAccessLevels.packages.sales.manageQuotes}
                                        update={operatorAccessLevels.packages.sales.manageQuotes
                                            ? updateOrdersShippingInformation
                                            : undefined}
                                        onFreightCompanyChanged={onFreightCompanyChanged}
                                        customerId={customerId}
                                    />
                                </Col>
                            }
                        </div>
                        <div className='w-100 my-4'/>
                        <div className='w-100 flex-grow-1'>
                            <Tabs value={tab} onChange={onTabChange}>
                                {
                                    tabsList.map(tab => (
                                        <Tab
                                            key={tab.value}
                                            label={tab.label}
                                            value={tab.value}
                                        />
                                    ))
                                }
                            </Tabs>
                        </div>
                        <div className='my-3 w-100'/>
                        {
                            tab === tabs.products &&
                            <QuoteInformationViewProductsSection
                                order={order}
                                setOrder={setOrder}
                                orderLoading={loading}
                                warehouses={warehouses}
                                canEditUnitPrice={canEditUnitPrice}
                                eligiblePriceChangePct={eligiblePriceChangePct}
                                productAvailabilities={productAvailabilities}
                                getProductsAvailabilities={getProductsAvailabilities}
                                setSubmittingDisabled={setSubmittingDisabled}
                            />
                        }
                        {
                            tab === tabs.billingAddress &&
                            <OrderInformationViewEditAddressesSection
                                address={order?.billingAddress}
                                updateAddress={updateAddress}
                                loading={loading}
                                typeId={CustomerAddressTypes.billing}
                                customerId={customerId}
                                customerName={defaultCustomer?.name}
                            />
                        }
                        {
                            tab === tabs.shippingAddress &&
                            <OrderInformationViewEditAddressesSection
                                address={order?.shippingAddress}
                                updateAddress={updateAddress}
                                onAddressUpdated={getFreightEstimation}
                                loading={loading}
                                typeId={CustomerAddressTypes.shipping}
                                customerId={customerId}
                                customerName={defaultCustomer?.name}
                            />
                        }
                        {
                            tab === tabs.addresses &&
                            <OrderInformationViewReadOnlyAddressesSection
                                billing={order?.billingAddress}
                                shipping={order?.shippingAddress}
                                loading={loading}
                            />
                        }
                        {
                            tab === tabs.transactions &&
                            <OrderInformationViewTransactionsSection
                                appliedTransactions={order?.appliedTransactions}
                                loading={loading}
                                orderId={orderId}
                                customerId={customerId}
                                type={OrderInformationViewTransactionsTypes.order}
                            />
                        }
                        {
                            tab === tabs.notes &&
                            <OrderInformationViewNotesSection
                                orderNo={order?.orderNo}
                                loading={loading}
                                placeType={OrderNoteAutoDefineTypes.quote}
                                editable={operatorAccessLevels.packages.sales.manageQuotes}
                            />
                        }
                        <div className='my-3 w-100'/>
                    </>
                }
            </div>
            <SendOrderEmailDialog
                open={sendOrderEmailDialogState.open}
                close={() => setSendOrderEmailDialogState(initSendOrderEmailDialogState)}
                send={sendEmail}
                customerId={customerId}
            />
            <CreateMissingItemsInWarehousesDialog
                open={CreateMissingItemsInWarehousesDialogState.open}
                items={CreateMissingItemsInWarehousesDialogState.items}
                close={CreateMissingItemsInWarehousesDialogState.close}
                warehouses={warehouses}
            />
            <PrintOrderConfigurationsDialog
                open={printOrderConfigurationsDialog.open}
                onClose={printOrderConfigurationsDialog.onClose}
                printUrl={printOrderConfigurationsDialog.printUrl}
            />
            <UpsertOrderInfoDialog
                open={upsertOrderInfoDialog.open}
                close={upsertOrderInfoDialog.close}
                entityToShow={upsertOrderInfoDialog.entityToShow}
                update={upsertOrderInfoDialog.update}
                salesPerson={upsertOrderInfoDialog.salesPerson}
                territory={upsertOrderInfoDialog.territory}
                accountManager={upsertOrderInfoDialog.accountManager}
                termsCode={upsertOrderInfoDialog.termsCode}
            />
            {
                operatorAccessLevels.packages.sales.createOrderFromAnother &&
                <DuplicateOrderDialog
                    recordId={orderId}
                    customerId={customerId}
                    variant={DuplicateOrderVariants.Quotes}
                    open={duplicateDialogOpen}
                    close={() => setDuplicateDialogOpen(false)}
                />
            }
        </>
    );
}

export default QuoteInformationView;
